import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getAdminSaleOrder, updateOrderStatus, type AdminSaleOrderWithEvent } from '../lib/api'
import { useConfirm } from '../components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

const STATUS_LABEL: Record<string, string> = {
  pending: 'Menunggu Konfirmasi',
  confirmed: 'Dikonfirmasi',
  rejected: 'Ditolak',
}
const STATUS_CLASS: Record<string, string> = {
  pending: 'bg-yellow-900 text-yellow-400',
  confirmed: 'bg-green-900 text-green-400',
  rejected: 'bg-red-900 text-red-400',
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-zinc-300">{children}</div>
    </div>
  )
}

export default function SaleOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const { token } = useAuth()
  const navigate = useNavigate()
  const { confirm, dialog: confirmDialog } = useConfirm()
  const [order, setOrder] = useState<AdminSaleOrderWithEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  async function load() {
    if (!orderId || !token) return
    setLoading(true)
    try {
      setOrder(await getAdminSaleOrder(token, orderId))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [orderId, token])

  async function handleStatus(status: 'confirmed' | 'rejected') {
    if (!token || !orderId) return
    const label = status === 'confirmed' ? 'Konfirmasi order ini?' : 'Tolak order ini? Tindakan ini tidak dapat dibatalkan.'
    const ok = await confirm(label, {
      title: status === 'confirmed' ? 'Konfirmasi Order' : 'Tolak Order',
      confirmLabel: status === 'confirmed' ? 'Konfirmasi' : 'Tolak',
    })
    if (!ok) return
    setUpdating(true)
    try {
      await updateOrderStatus(token, orderId, status)
      await load()
    } finally {
      setUpdating(false)
    }
  }

  if (loading) return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  if (!order) return <div className="p-6"><p className="text-zinc-500 text-sm">Order tidak ditemukan.</p></div>

  const fmt = (n: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n)

  const phase = order.sale_event_phases
  const total = phase ? phase.price * order.quantity : null

  const waPhone = order.buyer_phone.replace(/^0/, '62').replace(/[^0-9]/g, '')

  return (
    <div className="p-6">
      {confirmDialog}

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
          >
            <ChevronLeft size={14} />
            Kembali
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white font-mono">{order.order_code}</h1>
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_CLASS[order.status]}`}>
              {STATUS_LABEL[order.status]}
            </span>
          </div>
        </div>
      </div>

      {/* Split Panel */}
      <div className="flex gap-6 items-start">

        {/* LEFT PANEL */}
        <div className="w-[420px] shrink-0 space-y-4">

          {/* Info Event */}
          <Section title="Info Event">
            <InfoRow label="Event">
              <button
                onClick={() => navigate(`/sales/sale-events/${order.sale_event_id}/overview`)}
                className="text-yellow-400 hover:text-yellow-300 transition-colors text-left"
              >
                {order.sale_events.title}
              </button>
            </InfoRow>
            <InfoRow label="Tanggal">
              {[order.sale_events.date, order.sale_events.time].filter(Boolean).join(' · ')}
            </InfoRow>
            <InfoRow label="Lokasi">{order.sale_events.location}</InfoRow>
          </Section>

          {/* Info Pembeli */}
          <Section title="Info Pembeli">
            <InfoRow label="Nama">{order.buyer_name}</InfoRow>
            <InfoRow label="Nomor HP">
              <div className="flex items-center gap-2">
                <span>{order.buyer_phone}</span>
                <a
                  href={`https://wa.me/${waPhone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-semibold text-green-400 border border-green-800 rounded px-1.5 py-0.5 hover:border-green-600 transition-colors"
                >
                  WA
                </a>
              </div>
            </InfoRow>
            <InfoRow label="Email">{order.buyer_email}</InfoRow>
            {order.notes && <InfoRow label="Catatan">{order.notes}</InfoRow>}
          </Section>

          {/* Detail Pesanan */}
          <Section title="Detail Pesanan">
            {phase && <InfoRow label="Phase">{phase.name}</InfoRow>}
            <InfoRow label="Jumlah Tiket">{order.quantity} tiket</InfoRow>
            {phase && <InfoRow label="Harga / Tiket">{fmt(phase.price)}</InfoRow>}
            {total !== null && (
              <InfoRow label="Total Bayar">
                <span className="text-yellow-400 font-semibold text-base">{fmt(total)}</span>
              </InfoRow>
            )}
            <InfoRow label="Tanggal Order">
              {new Date(order.created_at).toLocaleString('id-ID')}
            </InfoRow>
            {order.confirmed_at && (
              <InfoRow label="Dikonfirmasi">
                {new Date(order.confirmed_at).toLocaleString('id-ID')}
              </InfoRow>
            )}
          </Section>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex-1 min-w-0">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800">
              <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Bukti Pembayaran</h2>
            </div>
            <div className="p-5 space-y-4">
              {/* Payment proof image */}
              <div>
                <img
                  src={normalizeImageKitUrl(order.proof_image_url)}
                  alt="Bukti pembayaran"
                  className="w-full rounded-lg border border-zinc-700 object-contain max-h-[480px]"
                />
                <a
                  href={normalizeImageKitUrl(order.proof_image_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  ↗ Buka di tab baru
                </a>
              </div>

              {/* Action buttons */}
              {order.status === 'pending' && (
                <>
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="text-xs text-zinc-500 mb-3">Tindakan</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleStatus('confirmed')}
                        disabled={updating}
                        className="flex-1 py-2.5 text-sm font-semibold bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        ✓ Konfirmasi
                      </button>
                      <button
                        onClick={() => handleStatus('rejected')}
                        disabled={updating}
                        className="flex-1 py-2.5 text-sm font-semibold bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        ✗ Tolak
                      </button>
                    </div>
                  </div>
                </>
              )}

              {order.status !== 'pending' && (
                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-xs text-zinc-500 mb-1">Status</p>
                  <span className={`inline-block rounded px-3 py-1 text-xs font-bold uppercase tracking-widest ${STATUS_CLASS[order.status]}`}>
                    {STATUS_LABEL[order.status]}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
